import React, { useEffect, useState } from 'react';

import { usePageTitle } from '@app/hooks';
import { Button, Callout, Classes, FormGroup, Intent, Spinner, Switch } from '@blueprintjs/core';
import FixedWidthPageContainer from '@components/FixedWidthPageContainer';
import Flex from '@components/Flex';
import Heading from '@components/Heading';
import DevText from '@components/Text';
import {
    getClientInactivityAlertSettings,
    runClientInactivityAlertCheck,
    updateClientInactivityAlertSettings,
} from '@data/api/clientInactivityAlert';

import {
    ClientInactivityAlertSettingsFormErrors,
    ClientInactivityAlertSettings as ClientInactivityAlertSettingsValues,
    DEFAULT_CLIENT_INACTIVITY_ALERT_SETTINGS,
    THRESHOLD_FIELDS,
    TOGGLE_FIELDS,
    describeRunOutcome,
    hasSettingsFormErrors,
    parseRunResult,
    parseSettingsForm,
    parseSettingsResource,
    toSettingsForm,
    toSettingsPayload,
    validateSettingsForm,
} from './utils/clientInactivityAlertSettings';

import { ThresholdFields, ToggleSection } from './styled';

const ClientInactivityAlertSettings: React.FC = () => {
    usePageTitle('Inactive client alert settings');

    const [form, setForm] = useState(() => toSettingsForm(DEFAULT_CLIENT_INACTIVITY_ALERT_SETTINGS));
    const [errors, setErrors] = useState<ClientInactivityAlertSettingsFormErrors>({});
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [isRunning, setIsRunning] = useState(false);
    const [isSaved, setIsSaved] = useState(false);
    // The backend stays authoritative: a failed GET shows this warning rather than pretending
    // the defaults on screen are what is stored
    const [loadError, setLoadError] = useState<string | null>(null);
    const [saveError, setSaveError] = useState<string | null>(null);
    const [runMessage, setRunMessage] = useState<string | null>(null);
    const [runIntent, setRunIntent] = useState<Intent>(Intent.SUCCESS);

    const applySettings = (settings: ClientInactivityAlertSettingsValues) => {
        setForm(toSettingsForm(settings));
        setErrors({});
    };

    useEffect(() => {
        let isMounted = true;

        getClientInactivityAlertSettings()
            .then((resource) => {
                if (!isMounted) {
                    return;
                }
                applySettings(parseSettingsResource(resource));
                setLoadError(null);
            })
            .catch(() => {
                if (!isMounted) {
                    return;
                }
                setLoadError(
                    'These settings could not be loaded. The values below are defaults, not what is saved — reload before changing anything.'
                );
            })
            .finally(() => {
                if (isMounted) {
                    setIsLoading(false);
                }
            });

        return () => {
            isMounted = false;
        };
    }, []);

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        const nextErrors = validateSettingsForm(form);
        setErrors(nextErrors);

        const settings = parseSettingsForm(form);
        if (hasSettingsFormErrors(nextErrors) || settings === null) {
            setIsSaved(false);
            return;
        }

        setIsSaving(true);
        setIsSaved(false);
        setSaveError(null);
        setRunMessage(null);

        try {
            // Re-render from what the server now stores, never from what we hoped it stored
            const saved = await updateClientInactivityAlertSettings(toSettingsPayload(settings));
            applySettings(parseSettingsResource(saved));
            setLoadError(null);
            setIsSaved(true);
        } catch {
            setSaveError('Settings could not be saved. Nothing was changed.');
        } finally {
            setIsSaving(false);
        }
    };

    const handleRunNow = async () => {
        setIsRunning(true);
        setRunMessage(null);
        setIsSaved(false);

        try {
            const outcome = parseRunResult(await runClientInactivityAlertCheck());
            setRunIntent(outcome.isDisabled ? Intent.WARNING : Intent.SUCCESS);
            setRunMessage(describeRunOutcome(outcome));
        } catch {
            setRunIntent(Intent.DANGER);
            setRunMessage('The alert check could not be run. Nothing was sent.');
        } finally {
            setIsRunning(false);
        }
    };

    if (isLoading) {
        return (
            <Flex justify="center">
                <Spinner />
            </Flex>
        );
    }

    return (
        <FixedWidthPageContainer>
            <Heading type="h3" className="mb-2">
                Inactive client alert settings
            </Heading>

            <DevText muted className="mb-3">
                These settings apply to everyone and are checked once a day at midnight Pacific time, for clients on an
                active paid subscription.
            </DevText>

            {loadError && (
                <Callout className="mb-2" intent={Intent.WARNING}>
                    {loadError}
                </Callout>
            )}

            <form onSubmit={handleSubmit}>
                <ThresholdFields>
                    {THRESHOLD_FIELDS.map(({ key, label, inputId }) => (
                        <FormGroup
                            key={key}
                            label={label}
                            labelFor={inputId}
                            intent={errors[key] ? Intent.DANGER : Intent.NONE}
                            helperText={errors[key]}
                        >
                            <input
                                id={inputId}
                                type="number"
                                step={1}
                                min={0}
                                className={`${Classes.INPUT} ${errors[key] ? Classes.INTENT_DANGER : ''}`}
                                value={form[key]}
                                onChange={(e) => {
                                    setIsSaved(false);
                                    setForm((previous) => ({ ...previous, [key]: e.target.value }));
                                }}
                            />
                        </FormGroup>
                    ))}
                </ThresholdFields>

                <ToggleSection>
                    {TOGGLE_FIELDS.map(({ key, label }) => (
                        <Switch
                            key={key}
                            label={label}
                            checked={form[key]}
                            onChange={() => {
                                setIsSaved(false);
                                setForm((previous) => ({ ...previous, [key]: !previous[key] }));
                            }}
                        />
                    ))}
                </ToggleSection>

                <Button
                    className="mt-2"
                    type="submit"
                    intent={Intent.PRIMARY}
                    loading={isSaving}
                    disabled={isSaving}
                    text="Update"
                />

                <Button
                    className="mt-2 ml-1"
                    type="button"
                    icon="refresh"
                    loading={isRunning}
                    disabled={isRunning || isSaving}
                    onClick={handleRunNow}
                    text="Run alert check now"
                />
            </form>

            {isSaved && (
                <Callout className="mt-2" intent={Intent.SUCCESS}>
                    Settings saved.
                </Callout>
            )}

            {saveError && (
                <Callout className="mt-2" intent={Intent.DANGER}>
                    {saveError}
                </Callout>
            )}

            {runMessage && (
                <Callout className="mt-2" intent={runIntent}>
                    {runMessage}
                </Callout>
            )}
        </FixedWidthPageContainer>
    );
};

export default ClientInactivityAlertSettings;
